import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  Upload, 
  FileText, 
  Download, 
  Trash2, 
  Save, 
  AlertCircle,
  Target,
  Users,
  Calendar,
  DollarSign,
  Shield,
  CheckCircle2,
  StickyNote,
  X
} from 'lucide-react';

const ProjectSettings = ({ project, isOpen, onClose, onUpdate }) => {
  const [settings, setSettings] = useState({
    scope_of_work_text: '',
    project_objectives: '',
    key_deliverables: '',
    timeline_notes: '',
    stakeholders: '',
    budget_notes: '',
    risk_factors: '',
    success_criteria: '',
    additional_notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [scopeFiles, setScopeFiles] = useState([]);
  const [activeTab, setActiveTab] = useState('scope');
  const [teamMembers, setTeamMembers] = useState([]);
  const [projectManager, setProjectManager] = useState('');

  useEffect(() => {
    if (isOpen && project?.id) {
      loadProjectSettings();
      loadTeamMembers();
      // Initial value from project prop
      setProjectManager(project?.project_manager_id || '');
    }
  }, [isOpen, project?.id, project?.project_manager_id]);

  const loadProjectSettings = async () => {
    try {
      const response = await api.get(`/projects/${project.id}/settings`);
      
      setSettings({
        project_name: response.data.project_name || '',
        project_description: response.data.project_description || '',
        scope_of_work_text: response.data.scope_of_work_text || '',
        project_objectives: response.data.project_objectives || '',
        key_deliverables: response.data.key_deliverables || '',
        timeline_notes: response.data.timeline_notes || '',
        stakeholders: response.data.stakeholders || '',
        budget_notes: response.data.budget_notes || '',
        risk_factors: response.data.risk_factors || '',
        success_criteria: response.data.success_criteria || '',
        additional_notes: response.data.additional_notes || ''
      });
      
      // Also set project manager from settings response if available
      if (response.data.project_manager_id) {
        setProjectManager(response.data.project_manager_id);
      }
      
      setScopeFiles(response.data.scope_of_work_files || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load project settings:', error);
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await api.get('/team');
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  };

  const handleSaveProjectManager = async () => {
    try {
      await api.put(`/projects/${project.id}`, {
        ...project,
        project_manager_id: projectManager || null
      });
      
      if (onUpdate) {
        onUpdate();
      }
      
      alert('Project manager updated successfully!');
    } catch (error) {
      console.error('Failed to update project manager:', error);
      alert('Failed to update project manager. Please try again.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${project.id}/settings`, settings);
      
      // Call onUpdate if provided to refresh parent data
      if (onUpdate) {
        onUpdate();
      }
      
      alert('Project settings saved successfully!');
    } catch (error) {
      console.error('Failed to save project settings:', error);
      alert('Failed to save project settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (limit to 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/projects/${project.id}/settings/upload-scope`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });

      setScopeFiles(prev => [...prev, response.data.file_url]);
      alert('File uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFile(false);
      event.target.value = ''; // Reset file input
    }
  };

  const handleDeleteFile = async (fileUrl) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
      await api.delete(`/projects/${project.id}/settings/scope-files?file_url=${encodeURIComponent(fileUrl)}`);

      setScopeFiles(prev => prev.filter(url => url !== fileUrl));
      alert('File deleted successfully!');
    } catch (error) {
      console.error('Failed to delete file:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'scope', label: 'Scope of Work', icon: FileText },
    { id: 'objectives', label: 'Objectives & Deliverables', icon: Target },
    { id: 'timeline', label: 'Timeline & Stakeholders', icon: Calendar },
    { id: 'budget', label: 'Budget & Risk', icon: DollarSign },
    { id: 'team', label: 'Team & Assignment', icon: Users },
    { id: 'additional', label: 'Additional Notes', icon: StickyNote }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900">Project Settings</h2>
            <p className="text-sm text-gray-600 mt-1 font-medium">{project?.name}</p>
            {settings.project_description && (
              <p className="text-sm text-gray-500 mt-1">{settings.project_description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-6 px-6 py-4 border-b border-gray-200 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading project settings...</div>
            </div>
          ) : (
            <>
              {/* Scope of Work Tab */}
              {activeTab === 'scope' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      <span>Scope of Work</span>
                    </h3>
                    
                    {/* File Upload Section */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Upload Files</h4>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-3">
                          Upload scope of work documents (PDF, DOC, TXT)
                        </p>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt,.md"
                          onChange={handleFileUpload}
                          disabled={uploadingFile}
                          className="hidden"
                          id="scope-file-upload"
                        />
                        <label
                          htmlFor="scope-file-upload"
                          className={`cursor-pointer inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors ${
                            uploadingFile ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingFile ? 'Uploading...' : 'Choose File'}
                        </label>
                      </div>
                      
                      {/* Uploaded Files */}
                      {scopeFiles.length > 0 && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files</h5>
                          <div className="space-y-2">
                            {scopeFiles.map((fileUrl, index) => {
                              const fileName = fileUrl.split('/').pop();
                              return (
                                <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                  <div className="flex items-center space-x-3">
                                    <FileText className="w-4 h-4 text-gray-500" />
                                    <span className="text-sm text-gray-700">{fileName}</span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <a
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-purple-600 hover:text-purple-800 transition-colors"
                                    >
                                      <Download className="w-4 h-4" />
                                    </a>
                                    <button
                                      onClick={() => handleDeleteFile(fileUrl)}
                                      className="text-red-600 hover:text-red-800 transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Text Area for Pasted Content */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Or Paste Scope of Work</h4>
                      <textarea
                        value={settings.scope_of_work_text}
                        onChange={(e) => handleInputChange('scope_of_work_text', e.target.value)}
                        placeholder="Paste your scope of work document here..."
                        rows={12}
                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Objectives & Deliverables Tab */}
              {activeTab === 'objectives' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    <span>Objectives & Deliverables</span>
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Objectives</label>
                    <textarea
                      value={settings.project_objectives}
                      onChange={(e) => handleInputChange('project_objectives', e.target.value)}
                      placeholder="Define the main objectives and goals for this project..."
                      rows={6}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Key Deliverables</label>
                    <textarea
                      value={settings.key_deliverables}
                      onChange={(e) => handleInputChange('key_deliverables', e.target.value)}
                      placeholder="List the main deliverables and outputs expected from this project..."
                      rows={6}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Success Criteria</label>
                    <textarea
                      value={settings.success_criteria}
                      onChange={(e) => handleInputChange('success_criteria', e.target.value)}
                      placeholder="Define how success will be measured for this project..."
                      rows={4}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Timeline & Stakeholders Tab */}
              {activeTab === 'timeline' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    <span>Timeline & Stakeholders</span>
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timeline Notes</label>
                    <textarea
                      value={settings.timeline_notes}
                      onChange={(e) => handleInputChange('timeline_notes', e.target.value)}
                      placeholder="Important dates, milestones, and timeline considerations..."
                      rows={5}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Stakeholders</label>
                    <textarea
                      value={settings.stakeholders}
                      onChange={(e) => handleInputChange('stakeholders', e.target.value)}
                      placeholder="Key stakeholders, their roles, and contact information..."
                      rows={5}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Budget & Risk Tab */}
              {activeTab === 'budget' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                    <span>Budget & Risk Management</span>
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Budget Notes</label>
                    <textarea
                      value={settings.budget_notes}
                      onChange={(e) => handleInputChange('budget_notes', e.target.value)}
                      placeholder="Budget constraints, cost estimates, and financial considerations..."
                      rows={5}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Risk Factors</label>
                    <textarea
                      value={settings.risk_factors}
                      onChange={(e) => handleInputChange('risk_factors', e.target.value)}
                      placeholder="Potential risks, dependencies, and mitigation strategies..."
                      rows={5}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}


              {/* Team & Assignment Tab */}
              {activeTab === 'team' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    <span>Team & Auto-Assignment</span>
                  </h3>

                  {/* Project Manager Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <span>Project Manager</span>
                    </h4>
                    
                    <p className="text-sm text-gray-600 mb-4">
                      Assign a project manager who will oversee this project. The PM can be automatically assigned to tasks based on auto-assignment rules.
                    </p>

                    <div className="flex items-end space-x-3">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Project Manager
                        </label>
                        <select
                          value={projectManager}
                          onChange={(e) => setProjectManager(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                        >
                          <option value="">No Project Manager</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name} ({member.email})
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={handleSaveProjectManager}
                        className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                      >
                        <Save className="w-4 h-4" />
                        <span>Save PM</span>
                      </button>
                    </div>

                    {projectManager && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-blue-300">
                        <p className="text-sm text-blue-800">
                          <CheckCircle2 className="w-4 h-4 inline mr-2" />
                          This project manager will be used when auto-assignment rules target &ldquo;Project Manager&rdquo;
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Auto-Assignment Info */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Target className="w-4 h-4 text-purple-600" />
                      <span>Auto-Assignment Rules</span>
                    </h4>
                    
                    <p className="text-sm text-gray-700 mb-4">
                      Auto-assignment rules are configured at the team level in <strong>Settings &gt; Workspace &gt; Auto Assignment</strong>. 
                      Rules can be set to automatically assign tasks based on:
                    </p>

                    <ul className="space-y-2 text-sm text-gray-600 ml-4">
                      <li className="flex items-start space-x-2">
                        <span className="text-purple-600 mt-1">•</span>
                        <span><strong>Project Manager:</strong> Assigns to the PM set above (with fallback to client PM)</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-purple-600 mt-1">•</span>
                        <span><strong>Task Priority:</strong> Route critical/high priority tasks to specific users</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-purple-600 mt-1">•</span>
                        <span><strong>Task Category:</strong> Assign based on task type (e.g., bugs to QA team)</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-purple-600 mt-1">•</span>
                        <span><strong>Status Changes:</strong> Trigger assignments when tasks move between stages</span>
                      </li>
                    </ul>

                    <div className="mt-4 pt-4 border-t border-purple-300">
                      <p className="text-sm text-gray-600 mb-3">
                        <AlertCircle className="w-4 h-4 inline mr-2 text-purple-600" />
                        To configure auto-assignment rules for this project:
                      </p>
                      <ol className="space-y-1 text-sm text-gray-600 ml-4">
                        <li>1. Go to Settings &gt; Workspace &gt; Auto Assignment</li>
                        <li>2. Create or edit rules</li>
                        <li>3. Use conditions to target this specific project</li>
                        <li>4. Select &ldquo;Project Manager&rdquo; as assignment type to use the PM set above</li>
                      </ol>
                    </div>
                  </div>

                  {/* Quick Info Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Current Project Manager</h5>
                      <p className="text-sm text-gray-600">
                        {projectManager ? (
                          teamMembers.find(m => m.id === projectManager)?.name || 'Unknown'
                        ) : (
                          <span className="text-gray-400">Not assigned</span>
                        )}
                      </p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Active Rules</h5>
                      <p className="text-sm text-gray-600">
                        Configure in team settings
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Notes Tab */}
              {activeTab === 'additional' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <StickyNote className="w-5 h-5 text-purple-600" />
                    <span>Additional Notes</span>
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                    <textarea
                      value={settings.additional_notes}
                      onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                      placeholder="Any additional notes, requirements, or considerations for this project..."
                      rows={10}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <AlertCircle className="w-4 h-4" />
            <span>Changes are saved automatically when you click Save</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 ${
                saving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save Settings'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettings;